import { z } from 'zod';
import {
  MAX_RECURRENCE_COUNT,
  MONTHLY_RECURRENCE_MODES,
  RECURRENCE_EDIT_SCOPES,
  RECURRENCE_FREQUENCIES,
  TASK_PRIORITIES,
  TASK_STATUSES,
  TASK_TYPES,
  type TaskPriority,
  type TaskStatus,
  type TaskType,
} from '../constants';
import { NullableText, RequiredText } from './common';
import { COLOR_TOKENS, type ColorToken } from './meta';

/** mine: assigned to me. all: every task I can see (tasks on leads follow lead visibility). */
export const TASK_SCOPES = ['mine', 'all'] as const;
export type TaskScope = (typeof TASK_SCOPES)[number];

/**
 * Due buckets, evaluated in the user's timezone (user.timezone ?? organization default):
 * overdue = due before today, today = due during today, upcoming = due after today, no_date = no due date.
 */
export const TASK_DUE_FILTERS = ['overdue', 'today', 'upcoming', 'no_date'] as const;
export type TaskDueFilter = (typeof TASK_DUE_FILTERS)[number];

const IsoInstant = z.iso.datetime({ offset: true });

const csvList = <T extends z.ZodTypeAny>(item: T) =>
  z.preprocess((v) => (typeof v === 'string' ? (v === '' ? [] : v.split(',')) : v), z.array(item));
const boolish = z.preprocess((v) => (v === 'true' || v === '1' ? true : v === 'false' || v === '0' ? false : v), z.boolean());

/** IANA time zone the times were entered in (the browser's). Repeating entries keep this wall-clock time across DST. */
export const TimeZoneName = z.string().trim().min(1).max(64);

/**
 * Minutes before the start when the assignee and guests get an in-app reminder; negative = after the start.
 * All-day entries start at local midnight, so "on the day at 9:00" is -540 and "the day before at 9:00" is 900.
 */
export const ReminderMinutes = z.number().int().min(-1440).max(40_320);

export const RecurrenceEnds = z.discriminatedUnion('type', [
  z.object({ type: z.literal('never') }),
  /** Last local day (YYYY-MM-DD, inclusive) on which an occurrence may start. */
  z.object({ type: z.literal('on'), date: z.iso.date() }),
  /** Total number of occurrences, the first one included. */
  z.object({ type: z.literal('after'), count: z.number().int().min(1).max(MAX_RECURRENCE_COUNT) }),
]);
export type RecurrenceEnds = z.infer<typeof RecurrenceEnds>;

/**
 * How an entry repeats, anchored on its first occurrence (the entry's own start, which is always an occurrence):
 * - daily: every `interval` days;
 * - weekly: every `interval` weeks (weeks start Monday) on `byWeekday` (ISO 1 = Monday … 7 = Sunday; defaults to
 *   the first occurrence's weekday);
 * - monthly: every `interval` months on the first occurrence's day of month / nth weekday / last weekday
 *   (`monthlyMode`, default day_of_month); months without that day are skipped (no 31st in April);
 * - yearly: every `interval` years on the first occurrence's month and day (Feb 29 → leap years only).
 * The API stores rules normalized: weekly always has `byWeekday` (sorted, unique), monthly always has
 * `monthlyMode`, other frequencies have neither.
 */
export const RecurrenceRule = z.object({
  freq: z.enum(RECURRENCE_FREQUENCIES),
  interval: z.number().int().min(1).max(99).default(1),
  byWeekday: z.array(z.number().int().min(1).max(7)).min(1).max(7).optional(),
  monthlyMode: z.enum(MONTHLY_RECURRENCE_MODES).optional(),
  ends: RecurrenceEnds.default({ type: 'never' }),
});
export type RecurrenceRule = z.output<typeof RecurrenceRule>;
export type RecurrenceRuleInput = z.input<typeof RecurrenceRule>;

/** GET /api/tasks. `scope` defaults to 'all' when filtering by lead/opportunity, else 'mine'. Notes are never listed. */
export const TaskListQuery = z.object({
  scope: z.enum(TASK_SCOPES).optional(),
  status: z.enum(TASK_STATUSES).default('open'),
  due: z.enum(TASK_DUE_FILTERS).optional(),
  businessId: z.uuid().optional(),
  opportunityId: z.uuid().optional(),
  /** 'me' | 'unassigned' | user id. */
  assignedTo: z.union([z.literal('me'), z.literal('unassigned'), z.uuid()]).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export type TaskListQuery = z.infer<typeof TaskListQuery>;

/** GET /api/tasks/summary — open-task counts per due bucket (tabs + sidebar badge). */
export const TaskSummaryQuery = z.object({ scope: z.enum(TASK_SCOPES).default('mine') });
export type TaskSummaryDTO = { overdue: number; today: number; upcoming: number; noDate: number };

/**
 * POST /api/tasks — a task or calendar entry. Cross-field rules (422 with a field path):
 * endAt needs dueAt and must be after it; allDay, notes, reminders and recurrence need dueAt. All-day entries
 * run from local midnight (dueAt) to local midnight after their last day (endAt, exclusive; defaults to one day).
 * Guests (`attendeeIds`) must be active users; the assignee is never listed as a guest.
 */
export const CreateTaskBody = z.object({
  businessId: z.uuid().nullable().optional(),
  contactId: z.uuid().nullable().optional(),
  opportunityId: z.uuid().nullable().optional(),
  type: z.enum(TASK_TYPES).default('follow_up'),
  title: RequiredText(300),
  description: NullableText(5000).optional(),
  /** Start of the entry (for a plain task: when it is due). */
  dueAt: IsoInstant.nullable().optional(),
  /** End of a time block (exclusive). Null = a point in time. */
  endAt: IsoInstant.nullable().optional(),
  allDay: z.boolean().default(false),
  location: NullableText(500).optional(),
  /** Overrides the calendar color (otherwise the type's color). */
  color: z.enum(COLOR_TOKENS).nullable().optional(),
  reminderMinutes: ReminderMinutes.nullable().optional(),
  priority: z.enum(TASK_PRIORITIES).default('normal'),
  /** Defaults to the current user. Assigning to someone else requires lead:assign. */
  assignedTo: z.uuid().nullable().optional(),
  /** Teammates invited to the entry; they see it on their calendar and get its reminder. */
  attendeeIds: z.array(z.uuid()).max(50).default([]),
  recurrence: RecurrenceRule.nullable().optional(),
  timeZone: TimeZoneName.optional(),
});
export type CreateTaskBody = z.input<typeof CreateTaskBody>;

/**
 * PATCH /api/tasks/:id — an entry's lead never changes, but a personal entry (no lead) can be put on a lead with
 * `businessId` (e.g. a quick calendar entry filled in later). For an occurrence of a repeating entry,
 * `scope` says which occurrences change: 'this' (only this one; changing `recurrence` is refused), 'following'
 * (this and later open occurrences) or 'all' (every open occurrence). Completed occurrences are history and never
 * change. `recurrence: null` with 'following'/'all' stops the repeat from that point. On a single entry,
 * `recurrence` turns it into a repeating one and `scope` is ignored.
 */
export const UpdateTaskBody = z
  .object({
    businessId: z.uuid(),
    contactId: z.uuid().nullable(),
    opportunityId: z.uuid().nullable(),
    type: z.enum(TASK_TYPES),
    title: RequiredText(300),
    description: NullableText(5000),
    dueAt: IsoInstant.nullable(),
    endAt: IsoInstant.nullable(),
    allDay: z.boolean(),
    location: NullableText(500),
    color: z.enum(COLOR_TOKENS).nullable(),
    reminderMinutes: ReminderMinutes.nullable(),
    priority: z.enum(TASK_PRIORITIES),
    assignedTo: z.uuid().nullable(),
    attendeeIds: z.array(z.uuid()).max(50),
    recurrence: RecurrenceRule.nullable(),
    timeZone: TimeZoneName,
  })
  .partial()
  .extend({ scope: z.enum(RECURRENCE_EDIT_SCOPES).default('this') });
export type UpdateTaskBody = z.input<typeof UpdateTaskBody>;

/** DELETE /api/tasks/:id?scope= — 'following'/'all' also delete later / all open occurrences of a repeating entry. */
export const DeleteTaskQuery = z.object({ scope: z.enum(RECURRENCE_EDIT_SCOPES).default('this') });
export type DeleteTaskQuery = z.input<typeof DeleteTaskQuery>;

export const CompleteTaskBody = z.object({
  /** Optional outcome note; saved as a note on the lead. */
  note: NullableText(5000).optional(),
});
export type CompleteTaskBody = z.input<typeof CompleteTaskBody>;

export type TaskDTO = {
  id: string;
  businessId: string | null;
  businessName: string | null;
  contactId: string | null;
  contactName: string | null;
  opportunityId: string | null;
  type: TaskType;
  title: string;
  description: string | null;
  dueAt: string | null;
  endAt: string | null;
  allDay: boolean;
  location: string | null;
  color: ColorToken | null;
  reminderMinutes: number | null;
  priority: TaskPriority;
  status: TaskStatus;
  assignedTo: string | null;
  /** Guests, excluding the assignee. */
  attendeeIds: string[];
  createdBy: string | null;
  completedAt: string | null;
  createdAt: string;
  /** Set on every occurrence of a repeating entry. */
  seriesId: string | null;
  /** The series' current rule (null when the entry does not repeat). */
  recurrence: RecurrenceRule | null;
};

// ----------------------------------------------------------------------------------------------- calendar

/** Widest window one calendar request may cover (a 6-week month grid is 42 days; the agenda looks 30 ahead). */
export const CALENDAR_MAX_RANGE_DAYS = 100;
/** Entries returned per request at most; `truncated` says when more exist. */
export const CALENDAR_MAX_ITEMS = 2000;

/** Whose calendar to show: 'me', 'unassigned' (no assignee) or a user id. */
export const CalendarPerson = z.union([z.literal('me'), z.literal('unassigned'), z.uuid()]);
export type CalendarPerson = z.infer<typeof CalendarPerson>;

/**
 * GET /api/calendar — dated entries overlapping [from, to): `dueAt < to` and (`dueAt >= from` or `endAt > from`).
 * An entry belongs to a person when it is assigned to them or they are a guest. Visibility is the task rule plus
 * guests (a guest always sees the entry, unless its lead was deleted). Cancelled entries are never returned.
 */
export const CalendarQuery = z
  .object({
    from: IsoInstant,
    to: IsoInstant,
    /** Comma-separated; empty or missing = me. At most 50. */
    users: csvList(CalendarPerson).pipe(z.array(CalendarPerson).max(50)).optional(),
    types: csvList(z.enum(TASK_TYPES)).optional(),
    includeCompleted: boolish.default(true),
    businessId: z.uuid().optional(),
  })
  .refine((q) => new Date(q.to).getTime() > new Date(q.from).getTime(), { path: ['to'], message: 'Must be after from' })
  .refine((q) => new Date(q.to).getTime() - new Date(q.from).getTime() <= CALENDAR_MAX_RANGE_DAYS * 86_400_000, {
    path: ['to'],
    message: `At most ${CALENDAR_MAX_RANGE_DAYS} days`,
  });
export type CalendarQuery = z.input<typeof CalendarQuery>;

export type CalendarResponse = { items: TaskDTO[]; truncated: boolean };

/** Reminders are returned from this long after they came due (a laptop that was asleep still shows them)… */
export const REMINDER_GRACE_MINUTES = 60;
/** …and this long before, so the client can fire them on the minute between polls. */
export const REMINDER_LOOKAHEAD_MINUTES = 15;

/** One pending reminder: an open entry of mine (assignee or guest) with `remindAt` = dueAt − reminderMinutes. */
export type CalendarReminderDTO = TaskDTO & { remindAt: string };
/** GET /api/calendar/reminders — remindAt within [now − grace, now + lookahead], earliest first. */
export type CalendarRemindersResponse = { items: CalendarReminderDTO[] };
